/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useMemo} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {CTCEntity} from '@ion/core/src/types/entities';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime, onClientErrorHandler} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

// defines the return type of useSearchById hook...
export type UseSearchResult = UseDefaultQueryResult & {
  result: CTCEntity;
  refresh: Function;
};

//defines the param definitions for useSearchById hook...
export interface UseSearchById {
  api: Function;
  id: string | number;
  baseKey: QueryKey;
  staleTime?: number;
  enabled?: boolean;
  defaultResponse?: any;
  refetchOnMount?: boolean | 'always';
};

/**
 * The useSearchById hook is a generic hook that handles all platform search
 * API calls which are based on fetching data by id.
 *
 * ```typescript
 *
 *  import {useSearchById} from '@ion/query/src/hooks/useSearchById';
 *
 *  const {result: asset} = useSearchById({
 *    api     : getAsset,
 *    id      : 12673,
 *    baseKey : QueryKey.GET_ASSET
 *  });
 *
 * ```
 */
export const useSearchById = ({
  api,
  id,
  baseKey,
  refetchOnMount = true,
  enabled = true,
  defaultResponse = {},
  staleTime = getStaleTime(5, false, false) as number
}: UseSearchById): UseSearchResult => {
  const queryClient = useQueryClient();

  const keys = useMemo(() => (
    createKey(baseKey, id)
  ), [baseKey, id]);

  const query = useQuery(keys, async() => {
    try {
      const result = await api(id);
      return result;
    } catch (err) {
      return onClientErrorHandler(err);
    }
  }, {staleTime, enabled, refetchOnMount});

  return createQueryResult(query, {
    result : query.data || defaultResponse,
    enabled
  }) as UseSearchResult;
};

export default useSearchById;
